import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to populate react-redux-form with dynamic default values",
  "description": "",
  "published": "2018-01-25T00:00:00.000Z",
  "redirect_from": ["/blog/populate-react-redux-form-dynamic-default-values/swizec/8158"],
  "categories": "Front End, JavaScript, react, redux, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "O0P7YIiQm4Y",
      "videostartat": "3579"
    }}></lite-youtube>
    <p>{`Here's a problem that took me embarrassingly long to solve: How do you populate dynamic values into a `}<a parentName="p" {...{
        "href": "https://github.com/davidkpiano/react-redux-form"
      }}>{`react-redux-form`}</a>{`?`}</p>
    <p>{`First of all, react-redux-form is not the same as `}<a parentName="p" {...{
        "href": "https://github.com/erikras/redux-form"
      }}>{`redux-form`}</a>{`. They're similar but not the same. The internet is confused about this issue, and that makes googling hard.`}</p>
    <p>{`Second of all, we're talking about a form driven by Redux. Pre-filling it with default values should be as easy as filling out its Redux state. Right?`}</p>
    <p>{`Well, forms live in their own reducers that you don't have direct access to. They use a bunch of internal state to mark inputs as dirty and focused and whatnot. It's a mess.`}</p>
    <p>{`Let's start at the beginning.`}</p>
    <h2 {...{
      "id": "a-simple-react-redux-form"
    }}>{`A simple react-redux-form`}</h2>
    <p>{`Here's a simple form built using react-redux-form. It has two input fields. When you Submit, it outputs your values to the console.`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "https://codepen.io/swizec/pen/Wdqgrp/"
      }}>{`ReactReduxForm - Quick Start`}</a>{` by Swizec Teller (`}<a parentName="p" {...{
        "href": "https://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "https://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>
    <p>{`Borrowed the code straight from `}<a parentName="p" {...{
        "href": "https://twitter.com/davidkpiano"
      }}>{`@davidkpiano's`}</a>{` official Quick Start example.`}</p>
    <p>{`Our form starts with a model and a Redux store:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const initialUserState = {
  firstName: "",
  lastName: "",
};

const store = createStore(
  combineForms({
    user: initialUserState,
  }),
  applyMiddleware(thunk)
);
`}</code></pre>
    <p><inlineCode parentName="p">{`initialUserState`}</inlineCode>{` describes the shape of a user and `}<inlineCode parentName="p">{`combineForms`}</inlineCode>{` creates a special reducer. That reducer takes care of driving the UI for our form and storing values.`}</p>
    <p>{`You'd think you could add default values to `}<inlineCode parentName="p">{`initialUserState`}</inlineCode>{`, and you can. Hold that thought… let's make sure we understand react-redux-form first :)`}</p>
    <p>{`The form itself comes in as a React component using `}<inlineCode parentName="p">{`<Control>`}</inlineCode>{` elements from react-redux-form.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class UserForm extends Component {
  handleSubmit(values) {
    console.log(values);
  }
  render() {
    return (
      <form model="user" onsubmit="{(values)" ==""> this.handleSubmit(values)}
      >
        <div class="field">
          <label>First name:</label>
          <control class="text" model="user.firstName">
        </control></div>

        <div class="field">
          <label>Last name:</label>
          <control class="text" model="user.lastName">
        </control></div>

        <button type="submit">
          Submit
        </button>
      </form>
    )
  }
}
`}</code></pre>
    <p><inlineCode parentName="p">{`handleSubmit`}</inlineCode>{` is the function we call when a user submits either by pressing the Submit button or hitting Enter. In our case, it prints values to the console.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`render`}</inlineCode>{` method uses a `}<inlineCode parentName="p">{`<Form>`}</inlineCode>{` component from react-redux-form. It comes with all the necessary Redux wiring so we don't have to worry about that.`}</p>
    <p>{`We use the children of that component to define how it renders. In our case, that's two `}<inlineCode parentName="p">{`div`}</inlineCode>{`s, some labels, a submit button, and two `}<inlineCode parentName="p">{`<Control.text>`}</inlineCode>{` components.`}</p>
    <p><inlineCode parentName="p">{`<Control.X>`}</inlineCode>{` components come with all the necessary wiring for our form to work. They'll handle focus, blur, default values, matching to correct parts of our model and so on. Everything we don't want to worry about manually.`}</p>
    <p>{`Wonderful 👌`}</p>
    <h2 {...{
      "id": "static-versus-dynamic-default-values"
    }}>{`Static versus Dynamic default values`}</h2>
    <p>{`Back to that obvious thought. Why can't you just use `}<inlineCode parentName="p">{`initialUserState`}</inlineCode>{` to define default values?`}</p>
    <p>{`You can. As long as your default values are static.`}</p>
    <p>{`Let me explain.`}</p>
    <p>{`Your default form values are static, if you know them in advance. Like when you're writing your code. This is pretty rare.`}</p>
    <p>{`What if you're building an edit form? You can't know values for your form until the user chooses what they're editing.`}</p>
    <p>{`And that's when trouble begins.`}</p>
    <h2 {...{
      "id": "fill-react-redux-form-with-dynamic-default-values"
    }}>{`Fill react-redux-form with dynamic default values`}</h2>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/lukeed05"
      }}>{`@lukeed05`}</a>{` helped me find the solution during livecoding. Thanks, mate.`}</p>
    <p>{`Here's how you do it:`}</p>
    <p><inlineCode parentName="p">{`react-redux-form`}</inlineCode>{` comes with a bunch of model actions. Actions its reducer understands. Actions it can use to do special things. Like populate your form.`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "https://codepen.io/swizec/pen/wpLEEq/"
      }}>{`ReactReduxForm - Dynamic Default Values`}</a>{` by Swizec Teller (`}<a parentName="p" {...{
        "href": "https://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "https://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>
    <p>{`Tap the "Change Defaults" button to pick a new random user. Its name will populate the form, and you can edit to your heart's content.`}</p>
    <p>{`To make that work, we used a Redux action generator called `}<inlineCode parentName="p">{`actions.merge`}</inlineCode>{`. It lets us merge the form model with a new set of default values.`}</p>
    <p>{`First we connect our `}<inlineCode parentName="p">{`UserForm`}</inlineCode>{` component to Redux.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const mapDispatchToProps = {
  setDefaultUser: (values) => actions.merge("user", values),
};
const ConnectedForm = connect(null, mapDispatchToProps)(UserForm);
`}</code></pre>
    <p>{`The usual stuff. Use `}<inlineCode parentName="p">{`connect`}</inlineCode>{` to connect to Redux, there's no props we need from state, and we use `}<inlineCode parentName="p">{`mapDispatchToProps`}</inlineCode>{` to add a dispatch function called `}<inlineCode parentName="p">{`setDefaultUser`}</inlineCode>{`.`}</p>
    <p><inlineCode parentName="p">{`setDefaultUser`}</inlineCode>{` is a curried application of `}<inlineCode parentName="p">{`actions.merge`}</inlineCode>{`. Takes a value object and merges it with the `}<inlineCode parentName="p">{`user`}</inlineCode>{` model in our store.`}</p>
    <p>{`I wired it to an `}<inlineCode parentName="p">{`onClick`}</inlineCode>{` callback in this example so you can try it multiple times.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`  changeUser() {
    this.props.setDefaultUser(
       randomUsers[Math.floor(Math.random()*randomUsers.length)]
    )
  }
`}</code></pre>
    <p>{`Another approach you can use is to call this action in `}<inlineCode parentName="p">{`componentDidMount`}</inlineCode>{`. That gives you true defaults – a form populated as soon as a user sees it.`}</p>
    <p>{`Happy hacking 🤓`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      